import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { createStyles, Box, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import { useSurgeons } from '@utilities/hooks/useSurgeons/useSurgeons';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { useHistory } from 'react-router-dom';
import { BaseEventTrackingWrapper } from '@components/BaseEventTrackingWrapper/BaseEventTrackingWrapper';
import { track } from '@amplitude/analytics-browser';
import { TrackableEventType } from '@components/BaseEventTrackingWrapper/BaseTrackableEvents.types';
import { ROUTE_CONSTANTS } from '@utilities/RouteConstants';
import TextLink from '@components/TextLink/TextLink';
import { getFullFormattedName } from '@utilities/commonFunction';
import { useGetBlockReleasesByNpi } from '@data/blockReleases/hooks/useGetBlockReleasesByNpi';
import { BlockReleaseApiResponse, BlockReleaseRequest } from '@data/blockReleases/types/BlockRelease.types';
import {
	combineFlipReleases
} from '@components/BlockRelease/utils/releaseFlipCombiner/combineFlipReleases';

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			display: 'flex',
			gap: theme.spacing(1),
			backgroundColor: theme.palette.primary[50],
			borderRadius: '12px',
			padding: theme.spacing(1.5, 2),
			justifyContent: 'space-between',
		},
		title: {
			fontWeight: 600,
			color: theme.palette.primary.main,
		},
		subtext: {
			fontWeight: 400,
		},
		textSpan: {
			fontWeight: 600,
			display: 'inline',
		},
		body: {
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
		},
		viewLink: {
			display: 'inline',
			paddingTop: theme.spacing(0),
		},
	}),
);

const dataField = 'blockNudge-blockNudgeBanner-viewLink';

const eventsToTrack = [ TrackableEventType.OnClick, TrackableEventType.OnKeyDown ];

export const BlockNudgeBanner: React.FC = () => {
	const classes = useStyles();
	const history = useHistory();
	const { npis, surgeonByNpi } = useSurgeons();
	const { getReleases, releases } = useGetBlockReleasesByNpi();

	React.useEffect(() => {
		if (npis.length) {
			const requestBody: BlockReleaseRequest = {
				query: 'RELEASE_BY_NPI',
				npis,
				types: [BlockReleaseState.RECOMMENDED_RELEASE],
			};
			getReleases(requestBody, { shouldDisableLoadIndicator: true });
		}
	}, [getReleases, npis]);

	const surgeonsWithRecommendedReleaseBlocks = React.useMemo(
		() => releases.filter(
			(release) => release.state === BlockReleaseState.RECOMMENDED_RELEASE
		),
		[releases],
	);

	const releasesWithFlipRecommendationsCombined: BlockReleaseApiResponse[] = React.useMemo(() => (
		combineFlipReleases(surgeonsWithRecommendedReleaseBlocks)
	), [surgeonsWithRecommendedReleaseBlocks]);

	const surgeonNames = React.useMemo(
		() => releasesWithFlipRecommendationsCombined.map((surgeonsWithRecommendedReleaseBlock) => {
			const surgeon = surgeonByNpi[surgeonsWithRecommendedReleaseBlock?.npis[0]];
			return getFullFormattedName(surgeon);
		}),
		[releasesWithFlipRecommendationsCombined, surgeonByNpi],
	);

	const { allExceptLast, lastSurgeon } = React.useMemo(() => {
		// Removes duplicates by converting surgeonNames to a set, then back to an array
		const uniqueSurgeonNames = Array.from(new Set(surgeonNames));

		//Displaying commas in-between multiple surgeon names, allowing addition of "and" before last surgeon name
		if (!uniqueSurgeonNames.length) return { allExceptLast: [], lastSurgeon: '' };

		return {
			allExceptLast: uniqueSurgeonNames.slice(0, -1),
			lastSurgeon: uniqueSurgeonNames[uniqueSurgeonNames.length - 1]
		};
	}, [surgeonNames]);

	const modifiedBlockCount = releasesWithFlipRecommendationsCombined.length;
	const blockText = `${modifiedBlockCount} Block${modifiedBlockCount !== 1 ? 's' : ''}`; //Conditional verbiage for single/multiple blocks

	const handleClickView = () => {
		history.push(ROUTE_CONSTANTS.BLOCK_RELEASE_RECOMMENDATION_LIST);
	};

	const handleTrackingEvent = (event: React.SyntheticEvent<HTMLElement>) => {
		const eventType = event.type;
		const eventName = `link-${eventType}`;

		track(eventName, { fieldName: dataField, eventType: eventType });
	};

	if (!surgeonNames.length) return null;

	return (
		<Box className={classes.root} aria-label="Block Nudge Banner" data-testid="block-nudge-banner">
			<InfoIcon color="primary" />
			<Box className={classes.body}>
				<Typography className={classes.title} >
					Review {blockText} for potential release to bring up your surgeon’s Block Utilization %
				</Typography>
				<Typography className={classes.subtext}>
					Blocks for{' '}
					<Typography className={classes.textSpan}>
						{allExceptLast.join(', ')}
					</Typography>
					{lastSurgeon && allExceptLast.length > 0 && ' and '}
					{lastSurgeon && (
						<Typography className={classes.textSpan}>
							{lastSurgeon}
						</Typography>
					)}{' '}
					have been identified and are at risk of being underutilized. If this
					time is not going to be used, please release it as soon as possible to
					improve their block utilization.
				</Typography>
			</Box>
			<BaseEventTrackingWrapper
				dataField={dataField}
				handleEvent={handleTrackingEvent}
				eventsToTrack={eventsToTrack}
			>
				<TextLink onClick={handleClickView} className={classes.viewLink}>View</TextLink>
			</BaseEventTrackingWrapper>
		</Box>
	);
};
